import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { translateFn } from '../../../../../utils/translator.helper';
import { UserFeaturesAndRights, UserRoleDependencies } from '../../../types';
import { classMap } from 'lit/directives/class-map.js';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Theme } from 'libs/shared/color-theme/src/lib/types';

/**
 * @summary Type here the description of this component
 * @tag user-role
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('user-role')
export class UserRoleWebComponent extends LitElement implements UserRoleDependencies {
  get handleDarkTheme(): () => void {
    return () => this._handleDarkTheme(Theme.AUTO); // or any default Theme
  }

  set handleDarkTheme(value: () => void) {
    this._handleDarkTheme = value;
  }

  @property({ type: String, reflect: true })
  userName: string;

  @state()
  userFeaturesAndRights: UserFeaturesAndRights = {
    manageCompanies: false,
    manageInventory: false,
    companyManager: false,
    manageReports: false,
    companion: false,
    sve_service: false,
    eps_service: false,
  };

  @property({ type: Boolean, reflect: true })
  isCloud = false;

  @state()
  isLoading = false;

  @queryAll('.theme-button')
  themeButtons: HTMLElement[];

  navigateToMyAccount: () => void;
  navigateToMyCompany: () => void;
  navigateToMyIntegrations: () => void;
  navigateToCredentialsManager: () => void;
  logoutUser: () => void;
  showFeedbackForm: () => void;
  showSupportPanel: () => void;
  private _handleDarkTheme: (mode: Theme) => void;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  setUserFeaturesAndRights(userFeaturesAndRights: any) {
    this.userFeaturesAndRights = { ...userFeaturesAndRights };
  }

  isCompanyHidden() {
    return !this.userFeaturesAndRights.companyManager || !this.isCloud;
  }

  isIntegrationsHidden() {
    return (
      (!this.userFeaturesAndRights.manageCompanies && !this.userFeaturesAndRights.manageInventory) ||
      this.userFeaturesAndRights.companion ||
      !this.isCloud
    );
  }

  isCredentialsManagerHidden() {
    return (
      !this.userFeaturesAndRights.manageInventory ||
      (!this.userFeaturesAndRights.manageReports &&
        !this.userFeaturesAndRights.sve_service &&
        (!this.userFeaturesAndRights.eps_service || !this.userFeaturesAndRights.sve_service))
    );
  }

  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  render() {
    return html` <div class="wrapper">
      <div class="top d-flex align-items-end justify-space-between">
        <div class="left">
          <gz-text fontSize="14" class="intro font-family-secondary">${translateFn('Welcome')},</gz-text>
          <gz-tooltip class="userName" isEllipsis tooltipText="${this.userName}">
            <gz-text fontSize="14" class="username font-family-secondary">${this.userName}</gz-text>
          </gz-tooltip>
        </div>
      </div>
      <div class="py-2">
        <div class="nav-links ${classMap({ 'is-loading': this.isLoading })}">
          <gz-progress-spinner></gz-progress-spinner>
          <gz-list-item class="font-family-secondary" @click="${() => this.navigateToMyAccount()}"
            >${translateFn('My account')}</gz-list-item
          >
          <gz-list-item
            class="font-family-secondary ${classMap({ 'd-none': this.isCompanyHidden() })}"
            @click="${() => this.navigateToMyCompany()}"
            >${translateFn('My company')}</gz-list-item
          >
          <gz-list-item
            class="font-family-secondary ${classMap({ 'd-none': this.isIntegrationsHidden() })}"
            @click="${() => this.navigateToMyIntegrations()}"
            >${translateFn('Integrations')}</gz-list-item
          >
          <gz-list-item
            class="font-family-secondary ${classMap({ 'd-none': this.isCredentialsManagerHidden() })}"
            @click="${() => this.navigateToCredentialsManager()}"
            >${translateFn('Credentials manager')}</gz-list-item
          >
          <gz-list-item class="font-family-secondary" @click="${() => this.showSupportPanel()}"
            >${translateFn('Help and support')}</gz-list-item
          >
          <gz-list-item class="font-family-secondary" @click="${() => this.showFeedbackForm()}"
            >${translateFn('Feedback')}</gz-list-item
          >
          ${this.renderHeaderActions()}
        </div>
      </div>
      <div class="bottom">
        <gz-list-item class="font-family-secondary" @click="${() => this.logoutUser()}"
          >${translateFn('Logout')}</gz-list-item
        >
      </div>
    </div>`;
  }

  private renderDarkThemeActions(): TemplateResult {
    const buttons = [
      { mode: Theme.AUTO, class: 'auto', icon: 'ds-icon-system_theme', tooltipText: 'System theme (default)' },
      { mode: Theme.LIGHT, class: 'light', icon: 'ds-icon-light_theme', tooltipText: 'Light theme' },
      { mode: Theme.DARK, class: 'dark', icon: 'ds-icon-dark_theme', tooltipText: 'Dark theme' },
      // Uncomment the following line to add High Contrast theme support
      // { mode: Theme.HIGH_CONTRAST, class: 'high_contrast', icon: 'ds-icon-high_contrast_theme', tooltipText: 'High Contrast theme' },
    ];

    return html`
      ${buttons.map(button => {
        return html` <gz-tooltip class="tooltip" tooltipText="${translateFn(button.tooltipText)}">
          <button
            @click="${() => this._handleDarkTheme(button.mode)}"
            icon="${button.icon}"
            class="theme-button ${button.class}"
          >
            <gz-icon name="${button.icon}" size="24" color="white" class="theme-button-icon"></gz-icon>
          </button>
        </gz-tooltip>`;
      })}
    `;
  }

  private renderHeaderActions(): TemplateResult {
    // TODO: remove `class` key for both actions when font-size-changer must be shown

    const actions = [
      { name: 'Font size', component: 'gz-font-size-changer', class: 'd-none' },
      { name: 'Theme', component: 'theme-buttons', class: 'visible' },
    ];

    return html`
      <div class="pt-3 header-actions">
        ${actions.map(action => {
          return html`<div class="d-flex justify-space-between header-action ${action.class}">
            <gz-text class="font-family-secondary" fontsize="${FontSizes.PARAGRAPH_12}"
              >${translateFn(action.name)}</gz-text
            >
            ${action.component === 'gz-font-size-changer'
              ? html`<gz-font-size-changer></gz-font-size-changer>`
              : this.renderDarkThemeActions()}
          </div>`;
        })}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'user-role': UserRoleWebComponent;
  }
}
